import { useCallback, useState, useRef, useEffect, type DragEvent, type ChangeEvent } from 'react'
import { Upload, X, ImageIcon } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Button } from './button'

interface ImageUploadProps {
  value?: string
  onChange?: (file: File | null, previewUrl: string | null) => void
  className?: string
  disabled?: boolean
  maxSize?: number // en MB
}

export function ImageUpload({
  value,
  onChange,
  className,
  disabled = false,
  maxSize = 10,
}: ImageUploadProps) {
  const [preview, setPreview] = useState<string | null>(value || null)
  const [isDragActive, setIsDragActive] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Actualizar preview cuando cambia value (imagen existente)
  useEffect(() => {
    if (value) {
      setPreview(value)
    }
  }, [value])

  const handleFile = useCallback(
    (file: File) => {
      setError(null)

      // Validar tipo
      if (!file.type.startsWith('image/')) {
        setError('El archivo debe ser una imagen')
        return
      }

      // Validar tamano
      if (file.size > maxSize * 1024 * 1024) {
        setError(`El archivo es muy grande. Maximo ${maxSize}MB`)
        return
      }

      // Crear preview local
      const localPreview = URL.createObjectURL(file)
      setPreview(localPreview)

      // Notificar al padre con el archivo y preview
      onChange?.(file, localPreview)
    },
    [onChange, maxSize]
  )

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragActive(false)

      if (disabled) return

      const file = e.dataTransfer.files[0]
      if (file) {
        handleFile(file)
      }
    },
    [disabled, handleFile]
  )

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(true)
  }, [])

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
  }, [])

  const handleClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click()
    }
  }, [disabled])

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        handleFile(file)
      }
      // Reset input para permitir seleccionar el mismo archivo
      e.target.value = ''
    },
    [handleFile]
  )

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    setPreview(null)
    setError(null)
    onChange?.(null, null)
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        className={cn(
          'relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors',
          isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25',
          disabled && 'cursor-not-allowed opacity-50',
          !disabled && 'cursor-pointer hover:border-primary/50'
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled}
        />

        {preview ? (
          <div className="relative">
            <img
              src={preview}
              alt="Preview"
              className="max-h-32 max-w-full rounded-lg object-contain"
            />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute -right-2 -top-2 h-6 w-6"
              onClick={handleRemove}
              disabled={disabled}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            {isDragActive ? (
              <ImageIcon className="h-10 w-10 text-primary" />
            ) : (
              <Upload className="h-10 w-10 text-muted-foreground" />
            )}
            <div className="text-center">
              <p className="text-sm font-medium">
                {isDragActive ? 'Suelta la imagen aqui' : 'Arrastra una imagen o haz clic'}
              </p>
              <p className="text-xs text-muted-foreground">
                PNG, JPG, GIF, WebP hasta {maxSize}MB
              </p>
            </div>
          </div>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
